import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { DisplayText, ToggleButton } from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { config } from 'ace-builds';
import { AccountClient } from '@nile/graphql';
import { useMutation, gql } from '@apollo/client';
import { AI_AGENTS_CONSTANT_FEATURE_ID_MAP } from '@nile/components';
import {
  fetchFeaturesFromAPI,
  updateFeatureViaAPI,
  ApiConfig
} from '@nile/components/src/Utils/aiFeatureApiUtils';
import {
  renderFeaturesList,
  handleToggleFeature as handleToggleFeatureUtil
} from '@nile/components/src/Components/AI/components/AIOnboarding/utils/aiFeatureUtils';
import PerplexityConfirmationModal from '@nile/banyan/src/Components/Cards/PerplexityConfirmationModal';

export const BubbleText: React.FC<{ text: React.ReactNode }> = ({ text }) => (
  <div className="flex justify-end mb10">
    <div className="ai-bubble p10 w-max-content">{text}</div>
  </div>
);

interface AiOnboardingFeaturesProps {
  featureType: string; // 'PROMPT' or 'RECOMMENDED_SOLUTION'
  config: {
    showBubble?: boolean;
    useSubFeatures?: boolean;
    title?: string;
    useAIFeaturesHook?: boolean;
    apiConfig?: ApiConfig;
    customComponent?: React.ComponentType<any>;
    customComponentProps?: Record<string, any>;
  };
}

export const FeaturesList: React.FC<AiOnboardingFeaturesProps> = ({
  featureType,
  config
}) => {
  const intl = useIntl();
  const [showBubble, setShowBubble] = useState(false);
  const [features, setFeatures] = useState<any[]>([]);
  const [featureData, setFeatureData] = useState<any>(null);
  const [showPerplexityModal, setShowPerplexityModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{
    featureId: string;
    currentState: string;
  } | null>(null);

  // Use AIFeaturesHook only when useAIFeaturesHook is true (default behavior)
  const aiHookData =
    config.useAIFeaturesHook !== false ? AIFeaturesHook() : null;
  const { getAIFeatureData, updateAIFeature, getAIFeaturesData } =
    aiHookData || {};

  // Initialize mutation for API-based approach when useAIFeaturesHook is false
  const shouldUseMutation =
    config.useAIFeaturesHook === false && config.apiConfig?.updateFeature;
  const [updateFeatureMutation] = useMutation(
    shouldUseMutation
      ? config.apiConfig.updateFeature
      : gql`
          mutation {
            __typename
          }
        `,
    {
      client: AccountClient,
      onCompleted: () => {
        if (config.useAIFeaturesHook === false && config.apiConfig) {
          handleFetchFeatures();
        }
      },
      onError: (error) => {
        console.error('Error updating feature via API:', error);
      }
    }
  );

  // Function to handle fetching features using the utility function
  const handleFetchFeatures = async () => {
    if (!config.apiConfig) return;

    const { featureData, features } = await fetchFeaturesFromAPI(
      config.apiConfig,
      featureType,
      config.useSubFeatures || false
    );

    setFeatureData(featureData);
    setFeatures(features);
  };

  useEffect(() => {
    if (config.useAIFeaturesHook === false) {
      // Use API-based approach
      handleFetchFeatures();
    } else {
      // Use existing AIFeaturesHook approach
      if (getAIFeatureData) {
        const data = getAIFeatureData(featureType);
        console.log('AI Feature Data:', data);
        setFeatureData(data);

        // Set initial features state based on config
        if (config.useSubFeatures && data?.subFeatures) {
          setFeatures(data.subFeatures);
        } else if (data?.features) {
          setFeatures(data.features);
        }
      }
    }
  }, [featureType, config.useSubFeatures, config.useAIFeaturesHook]);

  const handleToggleFeature = async (
    featureId: string,
    currentState: string
  ) => {
    if (config.useAIFeaturesHook === false) {
      // Use API-based approach only if mutation is properly configured
      if (shouldUseMutation) {
        await handleToggleFeatureUtil({
          featureId,
          currentState,
          updateFunction: async ({ input }) => {
            await updateFeatureViaAPI(
              updateFeatureMutation,
              input.featureId,
              input.state
            );
          },
          setFeatures
        });
      } else {
        console.error('API mutation not properly configured');
        return;
      }
    } else {
      // Use existing AIFeaturesHook approach
      if (updateAIFeature) {
        // Show confirmation modal for WEB_SEARCH feature when enabling
        if (
          featureId === AI_AGENTS_CONSTANT_FEATURE_ID_MAP['WEB_SEARCH'] &&
          currentState === 'INACTIVE'
        ) {
          setPendingToggle({ featureId, currentState });
          setShowPerplexityModal(true);
          return;
        }
        await handleToggleFeatureUtil({
          featureId,
          currentState,
          updateFunction: updateAIFeature,
          setFeatures
        });
        getAIFeaturesData();
      }
    }
  };

  const handlePerplexityConfirm = async () => {
    if (pendingToggle && updateAIFeature) {
      await handleToggleFeatureUtil({
        featureId: pendingToggle.featureId,
        currentState: pendingToggle.currentState,
        updateFunction: updateAIFeature,
        setFeatures
      });
    }
    setShowPerplexityModal(false);
    setPendingToggle(null);
  };

  const handlePerplexityCancel = () => {
    setShowPerplexityModal(false);
    setPendingToggle(null);
  };

  const handleCreatePrompt = (promptText: string) => {
    // Logic to create a new prompt using the entered text
    console.log('Creating prompt with text:', promptText);
    if (config.showBubble) {
      setShowBubble(true);
    }
  };

  const renderFeaturesListComponent = () => {
    return renderFeaturesList({
      features,
      onToggleFeature: handleToggleFeature,
      useSubFeatures: config.useSubFeatures
    });
  };

  return (
    <div className="mt-15">
      {/* Render title if provided and not using subfeatures */}
      {config.title && !config.useSubFeatures && (
        <div className="mb-20">
          <div className="mb-15">
            <DisplayText size="large" weight="Bold">
              {config.title}
            </DisplayText>
          </div>
        </div>
      )}

      {/* Features list */}
      <div className={config.useSubFeatures ? '' : 'mb-20'}>
        <div className="bg-newblack p10 br-5">
          {renderFeaturesListComponent()}
        </div>
      </div>

      {/* Render custom component if provided */}
      {config.customComponent &&
        React.createElement(config.customComponent, {
          ...config.customComponentProps,
          onCreatePrompt: handleCreatePrompt,
          features: features
        })}

      {/* Perplexity Confirmation Modal */}
      <PerplexityConfirmationModal
        open={showPerplexityModal}
        onClose={handlePerplexityCancel}
        onCancel={handlePerplexityCancel}
        onConfirm={handlePerplexityConfirm}
      />
    </div>
  );
};
